import type { ReactElement } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ControlRoomLayout } from "../layout/ControlRoomLayout";
import { AttachmentListPage } from "../../modules/attachments/AttachmentListPage";
import { LoginPage } from "../../modules/auth/LoginPage";
import { ConsignmentListPage } from "../../modules/consignments/ConsignmentListPage";
import { DashboardPage } from "../../modules/dashboard/DashboardPage";
import { BookingReportPage } from "../../modules/reports/BookingReportPage";
import { ManifestEntryPage } from "../../modules/operations/ManifestEntryPage";
import { GatePassPage } from "../../modules/operations/GatePassPage";
import { ManifestArrivalPage } from "../../modules/arrivals/ManifestArrivalPage";
import { DriverListPage } from "../../modules/drivers/DriverListPage";
import { FleetListPage } from "../../modules/fleet/FleetListPage";
import { MasterDataPage } from "../../modules/masterdata/MasterDataPage";
import { OperationsPage } from "../../modules/operations/OperationsPage";
import { AccountingPage } from "../../modules/accounting/AccountingPage";
import { PartiesPage } from "../../modules/parties/PartiesPage";
import { TripListPage } from "../../modules/trips/TripListPage";
import { EWayBillDashboardPage } from "../../modules/ewaybill/EWayBillDashboardPage";
import { CommissionPage } from "../../modules/commission/CommissionPage";
import { LorryHirePage } from "../../modules/lorryhire/LorryHirePage";
import UserManagementPage from "../../modules/users/UserManagementPage";
import SettingsPage from "../../modules/settings/SettingsPage";
import { BiltyStationeryPage } from "../../modules/stationery/BiltyStationeryPage";
import { AuditLogPage } from "../../modules/admin/AuditLogPage";
import { useAuthStore } from "../../stores/authStore";
import { PermissionGate } from "../../shared/components/PermissionGate";
import { AccountingEnquiryPage, InventoryEnquiryPage, OperationsEnquiryPage } from "../../modules/enquiries/EnquiryPages";
import { OperationsTransactionWorkspace } from "../../shared/components/OperationsTransactionTabs";

const Shell = () => (
  <ControlRoomLayout>
    <Outlet />
  </ControlRoomLayout>
);

const AuthenticatedShell = () => {
  const accessToken = useAuthStore((x) => x.accessToken);
  const location = useLocation();

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  }
  return <Shell />;
};

const PermissionRoute = ({
  permission,
  sectionName,
  children
}: {
  permission: string;
  sectionName: string;
  children: ReactElement;
}) => (
  <PermissionGate permission={permission} sectionName={sectionName}>
    {children}
  </PermissionGate>
);

const AdminOnlyRoute = ({ children }: { children: ReactElement }) => {
  const roles = useAuthStore((x) => x.user?.roles ?? []);
  const isAdmin = roles.some((role) => {
    const normalized = role.toLowerCase();
    return normalized === "admin" || normalized === "administrator" || normalized === "superadmin";
  });

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export const AppRoutes = () => {
  const accessToken = useAuthStore((x) => x.accessToken);
  const location = useLocation();
  const loginSearchParams = new URLSearchParams(location.search);
  const requestedReturnUrl = loginSearchParams.get("returnUrl");
  const loginRedirectTarget =
    requestedReturnUrl && requestedReturnUrl.startsWith("/")
      ? requestedReturnUrl
      : "/";

  return (
    <Routes>
      <Route path="/login" element={accessToken ? <Navigate to={loginRedirectTarget} replace /> : <LoginPage />} />
      <Route element={<AuthenticatedShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="/consignments" element={
          <PermissionRoute permission="Consignments.Read" sectionName="Consignment Booking">
            <OperationsTransactionWorkspace><ConsignmentListPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/trips" element={
          <PermissionRoute permission="Transport.Read" sectionName="Trip Planning">
            <TripListPage />
          </PermissionRoute>
        } />
        <Route path="/ewaybill" element={
          <PermissionRoute permission="Transport.Read" sectionName="E-Way Bills">
            <EWayBillDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/fleet" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Fleet Management">
            <FleetListPage />
          </PermissionRoute>
        } />
        <Route path="/drivers" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Driver Management">
            <DriverListPage />
          </PermissionRoute>
        } />
        <Route path="/attachments" element={
          <PermissionRoute permission="Transport.Read" sectionName="Document Vault">
            <AttachmentListPage />
          </PermissionRoute>
        } />
        <Route path="/operations" element={
          <PermissionRoute permission="Transport.Read" sectionName="Operational Utilities">
            <OperationsPage />
          </PermissionRoute>
        } />
        <Route path="/operations/enquiry" element={<PermissionRoute permission="Transport.Read" sectionName="Operations Enquiry"><OperationsEnquiryPage /></PermissionRoute>} />
        <Route path="/manifests" element={
          <PermissionRoute permission="Transport.Read" sectionName="Manifest Entry">
            <OperationsTransactionWorkspace><ManifestEntryPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/manifest-arrivals" element={
          <PermissionRoute permission="Transport.Read" sectionName="Manifest Arrival">
            <OperationsTransactionWorkspace><ManifestArrivalPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/gate-pass" element={
          <PermissionRoute permission="Transport.Read" sectionName="Gate Pass">
            <OperationsTransactionWorkspace><GatePassPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/reports" element={
          <PermissionRoute permission="Transport.Read" sectionName="Reports">
            <BookingReportPage />
          </PermissionRoute>
        } />
        <Route path="/master-data" element={
          <PermissionRoute permission="Transport.Read" sectionName="Master Data">
            <MasterDataPage />
          </PermissionRoute>
        } />
        <Route path="/accounting" element={
          <PermissionRoute permission="Accounting.Read" sectionName="Accounting">
            <AccountingPage />
          </PermissionRoute>
        } />
        <Route path="/accounting/enquiry" element={<PermissionRoute permission="Accounting.Read" sectionName="Accounting Enquiry"><AccountingEnquiryPage /></PermissionRoute>} />
        <Route path="/commission" element={
          <PermissionRoute permission="Accounting.Read" sectionName="Commission">
            <CommissionPage />
          </PermissionRoute>
        } />
        <Route path="/lorry-hire" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Lorry Hire">
            <OperationsTransactionWorkspace><LorryHirePage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/parties" element={
          <PermissionRoute permission="Parties.Read" sectionName="Parties">
            <PartiesPage />
          </PermissionRoute>
        } />
        <Route
          path="/users"
          element={
            <AdminOnlyRoute>
              <UserManagementPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminOnlyRoute>
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/bilty-stationery"
          element={
            <PermissionRoute permission="InventoryIssue.Read" sectionName="Document Stationery">
              <BiltyStationeryPage />
            </PermissionRoute>
          }
        />
        <Route path="/inventory/enquiry" element={<PermissionRoute permission="InventoryIssue.Read" sectionName="Inventory Enquiry"><InventoryEnquiryPage /></PermissionRoute>} />
        <Route
          path="/audit-logs"
          element={
            <AdminOnlyRoute>
              <AuditLogPage />
            </AdminOnlyRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to={accessToken ? "/" : "/login"} replace />} />
    </Routes>
  );
};
